/**
 * Platform-neutral design tokens (ELARA-701).
 *
 * WHY: These tokens are the single source of truth for the visual language. The web client renders
 * them into Tailwind v4's `@theme` block (`client/src/theme.generated.css`, produced by
 * `client/src/theme/render-theme-css.ts`) and the upcoming React Native shell will feed the same
 * objects into its NativeWind `tailwind.config`. Tailwind v4's `@theme`-in-CSS model has no
 * NativeWind equivalent, so the tokens have to live somewhere both platforms can `import` — a plain
 * TS module with no DOM/CSS/RN coupling — or the two platforms drift the moment a colour changes.
 *
 * This module is intentionally pure data: it compiles under `@elara/client-core`'s no-DOM lib and
 * carries no runtime behaviour. The interpretation of a value (a CSS `var()` reference on web, a
 * resolved literal on native) is the consumer's job, not this module's.
 *
 * Adding or changing a token here is the ONLY place a token should change. After editing, run
 * `pnpm gen:theme` to regenerate the web CSS (the pre-commit hook does this automatically, and a
 * test guards against the committed CSS drifting from this source).
 */

/**
 * Primitive "camel" colour ramp — the brand's core hue. These are concrete `hsl()` literals (the
 * only colours the project owns outright); Tailwind's default palette (`white`, `zinc-*`,
 * `yellow-*`, `red-*`) supplies the rest and is referenced by name from {@link semanticColors}.
 */
export const colorPalette = {
  'camel-50': 'hsl(38.2 45.1% 97.5%)',
  'camel-100': 'hsl(39.1 45.1% 93%)',
  'camel-150': 'hsl(39.1 45.1% 88%)',
  'camel-200': 'hsl(37.8 45.1% 83%)',
  'camel-300': 'hsl(38.3 45.1% 68%)',
  'camel-400': 'hsl(37.8 45.1% 58%)',
  'camel-500': 'hsl(38.1 45.1% 48%)',
  'camel-600': 'hsl(37.8 45.1% 38%)',
  'camel-700': 'hsl(38.3 45.1% 28%)',
  'camel-800': 'hsl(37.8 45.1% 18%)',
  'camel-900': 'hsl(39.1 45.1% 8%)',
  'camel-950': 'hsl(37.5 44.4% 6.1%)',
} as const;

/**
 * Semantic colour roles, per colour scheme. Values reference palette / Tailwind-default colours via
 * `var(--color-<name>)` so the light↔dark swap happens through the semantic variable (not by
 * editing primitives) — exactly the indirection the web `@theme`/`.dark` cascade relies on. The
 * `dark` set deliberately omits any role that should inherit its light value through `var()` (e.g.
 * `input-focus` resolves to the dark `accent` automatically), mirroring the original CSS.
 *
 * Native consumers (NativeWind) resolve these references against {@link colorPalette} plus the
 * Tailwind default palette at config time.
 */
export const semanticColors = {
  light: {
    'background-primary': 'var(--color-white)',
    'background-secondary': 'var(--color-camel-50)',
    'background-tertiary': 'var(--color-camel-100)',
    'background-quaternary': 'var(--color-camel-200)',
    background: 'var(--color-background-primary)',
    foreground: 'var(--color-camel-950)',
    card: 'var(--color-white)',
    'card-foreground': 'var(--color-camel-950)',
    popover: 'var(--color-white)',
    'popover-foreground': 'var(--color-camel-950)',
    primary: 'var(--color-camel-900)',
    'primary-hover': 'var(--color-yellow-600)',
    'primary-foreground': 'var(--color-white)',
    'primary-foreground-hover': 'var(--color-white)',
    secondary: 'var(--color-camel-100)',
    'secondary-hover': 'var(--color-camel-200)',
    'secondary-foreground': 'var(--color-camel-900)',
    muted: 'var(--color-camel-100)',
    'muted-foreground': 'var(--color-camel-700)',
    accent: 'var(--color-yellow-600)',
    'accent-hover': 'var(--color-yellow-700)',
    'accent-foreground': 'var(--color-white)',
    destructive: 'var(--color-red-600)',
    'destructive-hover': 'var(--color-red-700)',
    'destructive-foreground': 'var(--color-white)',
    // Green-700, not 600: white on green-600 is 3.2:1, under AA for button and toast text; on 700 it is 4.9:1.
    success: 'var(--color-green-700)',
    'success-hover': 'var(--color-green-800)',
    'success-foreground': 'var(--color-white)',
    border: 'var(--color-camel-100)',
    input: 'var(--color-camel-200)',
    'input-focus': 'var(--color-accent)',
    ring: 'var(--color-accent)',
  },
  dark: {
    'background-primary': 'var(--color-zinc-900)',
    'background-secondary': 'var(--color-zinc-800)',
    'background-tertiary': 'var(--color-zinc-900)',
    'background-quaternary': 'var(--color-zinc-800)',
    background: 'var(--color-background-primary)',
    foreground: 'var(--color-white)',
    card: 'var(--color-zinc-900)',
    'card-foreground': 'var(--color-white)',
    popover: 'var(--color-zinc-900)',
    'popover-foreground': 'var(--color-white)',
    primary: 'var(--color-white)',
    'primary-hover': 'var(--color-yellow-600)',
    'primary-foreground': 'var(--color-zinc-950)',
    'primary-foreground-hover': 'var(--color-white)',
    secondary: 'var(--color-zinc-800)',
    'secondary-hover': 'var(--color-zinc-700)',
    'secondary-foreground': 'var(--color-white)',
    muted: 'var(--color-zinc-800)',
    'muted-foreground': 'var(--color-zinc-400)',
    accent: 'var(--color-yellow-500)',
    'accent-hover': 'var(--color-yellow-700)',
    'accent-foreground': 'var(--color-white)',
    destructive: 'var(--color-red-200)',
    'destructive-hover': 'var(--color-red-300)',
    'destructive-foreground': 'var(--color-red-800)',
    success: 'var(--color-green-500)',
    'success-hover': 'var(--color-green-600)',
    'success-foreground': 'var(--color-zinc-950)',
    border: 'var(--color-zinc-700)',
    input: 'var(--color-zinc-700)',
    ring: 'var(--color-zinc-500)',
  },
} as const;

/**
 * Font-family stacks. `display` is the headline face; `sans` is the body stack (with the system
 * fallbacks the web build relies on). Maps to Tailwind's `--font-*` namespace (`font-display`,
 * `font-sans`).
 */
export const fontFamily = {
  display: "'itc-avant-garde-gothic-pro', 'Helvetica Neue', sans-serif",
  sans: "'aktiv-grotesk', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Helvetica Neue', 'Arial', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif",
} as const;

/**
 * Font-size overrides for the small end of the scale. `xs` shrinks Tailwind's default; `2xs` adds a
 * smaller step. Maps to Tailwind's `--text-*` namespace (`text-xs`, `text-2xs`).
 */
export const fontSize = {
  xs: '0.8125rem', // 13px
  '2xs': '0.75rem', // 12px
} as const;

/**
 * Letter-spacing tokens. `label` is the wide tracking used on eyebrow/label text. Emitted as the
 * bespoke `--label-tracking` custom property (consumed via `var(--label-tracking)`), so it is NOT
 * a Tailwind `tracking-*` utility.
 */
export const letterSpacing = {
  label: '0.08em',
} as const;

/** Base corner radius, emitted as the bespoke `--radius` custom property. */
export const radius = '4px';

/**
 * Box-shadow tokens, per colour scheme. `input`/`input-active` are bespoke (the underline-style
 * field affordance); `sm`/`md`/`lg` override Tailwind defaults and are re-tinted for dark mode. The
 * `dark` set only overrides the scale shadows — `input`/`input-active` reference theme colours via
 * `var()` and re-resolve in dark automatically.
 */
export const shadows = {
  light: {
    input: '0 1px 0 0 var(--color-border)',
    'input-active': '0 2px 0 0 var(--color-primary)',
    sm: '0 1px 2px 0 rgba(36, 31, 24, 0.05), 0 1px 4px 0 rgba(36, 31, 24, 0.05), 0 2px 8px 0 rgba(36, 31, 24, 0.05)',
    md: '0 2px 2px 0 rgba(36, 31, 24, 0.08), 0 2px 4px 0 rgba(36, 31, 24, 0.08), 0 3px 8px 0 rgba(36, 31, 24, 0.08)',
    // NOTE: the `240` blue channel is preserved verbatim from the original @theme block; changing it
    // to `24` would alter the rendered shadow, so it stays as-is until a deliberate design pass.
    lg: '0 10px 15px -3px rgba(36, 31, 240, 0.1), 0 4px 6px -2px rgba(36, 31, 24, 0.05)',
  },
  dark: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05), 0 1px 4px 0 rgba(0, 0, 0, 0.05), 0 2px 8px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.05)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  },
} as const;

/**
 * Motion tokens — named animation shorthands mapped to Tailwind's `--animate-*` namespace
 * (`animate-accordion-down`/`-up`). The referenced `@keyframes` live in `client/src/index.css`.
 */
export const motion = {
  'accordion-down': 'accordion-down 0.2s ease-out',
  'accordion-up': 'accordion-up 0.2s ease-out',
} as const;

/** Colour schemes the token set defines. */
export type ColorScheme = keyof typeof semanticColors;
